import { useState, useEffect } from 'react';
import { Fingerprint, Trash2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useWebAuthn } from '@/hooks/useWebAuthn';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';

export function BiometricSettings() {
  const { user } = useAuth();
  const {
    isSupported,
    checkBiometricAvailability,
    hasRegisteredBiometric,
    registerBiometric,
    removeBiometric,
    isLoading,
    isIframeBlocked,
  } = useWebAuthn();
  
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);

  useEffect(() => {
    const checkAvailability = async () => {
      if (isSupported) {
        const available = await checkBiometricAvailability();
        setBiometricAvailable(available);
        setIsRegistered(hasRegisteredBiometric());
      }
    };
    checkAvailability();
  }, [isSupported, checkBiometricAvailability, hasRegisteredBiometric]);

  const handleEnableBiometric = async () => {
    if (!user?.email) {
      toast({
        title: 'Error',
        description: 'No se encontró el email del usuario',
        variant: 'destructive',
      });
      return;
    }

    const success = await registerBiometric(user.email);
    if (success) {
      setIsRegistered(true);
      toast({
        title: 'Biometría habilitada',
        description: 'Ahora puedes usar Face ID o huella dactilar para iniciar sesión más rápido',
      });
    } else {
      toast({
        title: 'Error al configurar biometría',
        description: 'Por favor intenta de nuevo o verifica los permisos del dispositivo',
        variant: 'destructive',
      });
    }
  };

  const handleDisableBiometric = () => {
    removeBiometric();
    setIsRegistered(false);
    toast({
      title: 'Biometría deshabilitada',
      description: 'Se eliminó la autenticación biométrica de este dispositivo',
    });
  };

  if (!isSupported) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Fingerprint className="h-5 w-5" />
            Autenticación Biométrica
          </CardTitle>
          <CardDescription>
            {isIframeBlocked 
              ? 'La autenticación biométrica no está disponible en el modo de vista previa. Por favor accede directamente a la aplicación publicada para configurar Face ID o huella dactilar.'
              : 'Tu navegador no soporta autenticación biométrica (WebAuthn). Intenta usar un navegador más reciente o un dispositivo con soporte biométrico.'
            }
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!biometricAvailable) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Fingerprint className="h-5 w-5" />
            Autenticación Biométrica
          </CardTitle>
          <CardDescription>
            Este dispositivo no tiene autenticación biométrica disponible (Face ID, huella dactilar, etc.).
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Fingerprint className="h-5 w-5" />
          Autenticación Biométrica
        </CardTitle>
        <CardDescription>
          Usa Face ID o huella dactilar para iniciar sesión más rápido en este dispositivo.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isRegistered ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-primary">
              <CheckCircle className="h-5 w-5" />
              <span className="text-sm font-medium">Biometría habilitada en este dispositivo</span>
            </div>
            <Button 
              variant="outline" 
              onClick={handleDisableBiometric}
              className="w-full touch-manipulation"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Deshabilitar biometría
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Al habilitar la biometría, podrás usar Face ID o tu huella dactilar para acceder más rápido a tu cuenta desde este dispositivo.
            </p>
            <Button 
              onClick={handleEnableBiometric}
              disabled={isLoading}
              className="w-full touch-manipulation"
            >
              <Fingerprint className="mr-2 h-5 w-5" />
              {isLoading ? 'Configurando...' : 'Habilitar Face ID / Huella'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
